<script setup lang="ts">
/**
 * A from–to date range with one-click presets („Diese Woche“, „2 Wochen“, …)
 * and two date fields for a free range.
 *
 * The model is `{ from, to }` of ISO days, both inclusive; either end may be
 * `null` (open). The fields keep the range valid: `to` cannot be picked before
 * `from`, and moving `from` past `to` drags `to` along. A preset chip shows as
 * selected while the range is exactly its range.
 */
import { computed } from 'vue';
import NxChip from '../Chip/NxChip.vue';
import NxChipGroup from '../Chip/NxChipGroup.vue';
import type { InputHeight } from './internal';
import NxDateInput from './NxDateInput.vue';
import {
  defaultDateRangePresets,
  sameRange,
  todayIso,
  type NxDateRange,
  type NxDateRangePreset,
} from './dateRangePresets';

const props = withDefaults(
  defineProps<{
    /** Presets offered as chips. `[]` hides the row. */
    presets?: NxDateRangePreset[];
    /** Reference day for the presets (ISO). Defaults to today. */
    today?: string;
    fromLabel?: string;
    toLabel?: string;
    presetsLabel?: string;
    disabled?: boolean;
    height?: InputHeight;
    /** Forwarded to both fields, see `NxDateInput`. */
    markWeekends?: boolean;
    /** Forwarded to both fields, see `NxDateInput`. */
    holidays?: Readonly<Record<string, string>>;
    /** Forwarded to both fields, see `NxDateInput`. */
    locale?: string;
  }>(),
  {
    presets: () => defaultDateRangePresets(),
    today: undefined,
    fromLabel: 'von',
    toLabel: 'bis',
    presetsLabel: 'Zeitraum-Vorgaben',
    disabled: false,
    height: undefined,
    markWeekends: false,
    holidays: undefined,
    locale: undefined,
  }
);

const model = defineModel<NxDateRange>({ required: true });

const reference = computed(() => props.today ?? todayIso());

const activePreset = computed(
  () => props.presets.find((p) => sameRange(p.range(reference.value), model.value))?.id ?? null
);

const fromModel = computed<string | null>({
  get: () => model.value.from,
  set: (from) => {
    const to = from && model.value.to && model.value.to < from ? from : model.value.to;
    model.value = { from, to };
  },
});

const toModel = computed<string | null>({
  get: () => model.value.to,
  set: (to) => {
    model.value = { from: model.value.from, to };
  },
});

function pick(preset: NxDateRangePreset) {
  model.value = preset.range(reference.value);
}
</script>

<template>
  <div class="nx-date-range-input">
    <NxChipGroup v-if="presets.length" :aria-label="presetsLabel">
      <NxChip
        v-for="preset in presets"
        :key="preset.id"
        size="xs"
        :selected="activePreset === preset.id"
        :disabled="disabled"
        @click="pick(preset)"
      >
        {{ preset.label }}
      </NxChip>
    </NxChipGroup>
    <div class="nx-date-range-input__fields">
      <label class="nx-date-range-input__part">
        <span class="nx-date-range-input__label">{{ fromLabel }}</span>
        <NxDateInput
          v-model="fromModel"
          :disabled="disabled"
          :height="height"
          :locale="locale"
          :mark-weekends="markWeekends"
          :holidays="holidays"
          :today="reference"
        />
      </label>
      <label class="nx-date-range-input__part">
        <span class="nx-date-range-input__label">{{ toLabel }}</span>
        <NxDateInput
          v-model="toModel"
          :disabled="disabled"
          :height="height"
          :locale="locale"
          :min-value="model.from ?? undefined"
          :mark-weekends="markWeekends"
          :holidays="holidays"
          :today="reference"
        />
      </label>
    </div>
  </div>
</template>

<style scoped>
.nx-date-range-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: 100%;
}
.nx-date-range-input__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--nxt-space-xs);
}
.nx-date-range-input__part {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-date-range-input__label {
  flex-shrink: 0;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
</style>
